<template>
  <div
    class="
      main-product
      section-styles--bottom-padding
      section-styles--top-padding
      section-styles--divider
      critical-component-clear
    "
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="main-product__container grid">
      <!-- Gallery -->
      <div
        class="main-product__gallery-container col xs6 l7"
      >
        <product-gallery
          class="main-product__gallery"
          :brand-tile-background-colour="section.brandTileBackgroundColour"
          :brand-tiles="section.brandTiles"
          :current-media-position="currentMediaPosition"
          :current-namespace="currentNamespace"
          namespace="gallery"
          :product="localProduct"
          @update-current-media-position="handleUpdateCurrentMediaPosition"
        />

        <!-- Spacer to maintain layout -->
        <responsive-image
          class="main-product__gallery-spacer"
          :image="{}"
          ratio="3:4"
          :sizes="sizes"
        />
      </div>

      <!-- Details -->
      <div
        id="product-details"
        class="main-product__details-container col xs6 l5"
        :js-sign-post="$string('accessibility.sign_posts.product_details')"
      >
        <product-details
          class="main-product__details"
          :product="localProduct"
          :variant="variant"
        />

        <product-form
          class="main-product__form"
          :low-stock-threshold="section.lowStockThreshold"
          :matched-brand="sizeGuide"
          :product="localProduct"
          :variant="variant"
          @update-variant="handleVariantUpdate"
        />

        <product-description
          class="main-product__description"
          :product="localProduct"
          :trustpilot-widget="section.trustpilotWidget"
          :usps="section.usps"
        />
      </div>
    </div>

    <!-- Product gallery modal -->
    <overlay
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="{ isActive }">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-media-position="currentMediaPosition"
          :current-namespace="currentNamespace"
          :is-active="isActive"
          namespace="modal"
          :product="localProduct"
          @update-current-media-position="handleUpdateCurrentMediaPosition"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>

    <!-- Size guide drawer -->
    <overlay
      v-if="sizeGuide"
      key="sizeGuide"
      block-class="size-guide-tables-overlay"
      direction="left"
      namespace="sizeGuide"
      type="drawer"
    >
      <template #title>
        <span
          class="text-heading-2xs text-heading-xs-desktop"
          v-text="$string('product.form.size_guide')"
        />
      </template>

      <template #body>
        <size-guide-tables
          v-if="getComponentVisible('size-guide-tables')"
          v-cloak
          class="main-product__size-guide"
          :blocks="[sizeGuide]"
          :default-subtext="section.defaultSubtext"
          :default-units="section.defaultUnits"
          drawer
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductDescription from '~async/product-description/product-description'
import ProductDetails from '~async/product-details/product-details'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ResponsiveImage from '~global/images/responsive-image'

import breakpoints from '~/config/breakpoints'
import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    Overlay,
    ProductDescription,
    ProductDetails,
    ProductForm,
    ProductGallery,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
    ResponsiveImage,
    SizeGuideTables: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.size-guide-tables' */
        '~async/size-guide-tables/size-guide-tables'
      ),
    }),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    section: {
      type: Object,
      default: () => ({
        brandTiles: [],
        defaultSubtext: '',
        defaultUnits: 'in',
        lowStockThreshold: 5,
        trustpilotWidget: '',
        usps: [],
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      currentMediaPosition: 1,
      currentNamespace: 'gallery',
      fixedGallery: false,
      handle: cnvs.page.handle,
      localProduct: this.liquid,
      scrollListener: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute matched brand for size guide drawer.
     * @returns {Object|Boolean}
     */
    sizeGuide() {
      return this.liquid.size_guide || false
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 33vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch variant to update current media position if variant has media.
     */
    variant() {
      if (!this.variant.image) {
        return
      }

      const variantMedia = this.localProduct.media.find((media) => {
        return media.id === this.variant.image
      })

      if (!variantMedia) {
        return
      }

      this.currentMediaPosition = variantMedia.position
    },
  },

  mounted() {
    this.setProduct()
  },

  methods: {

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
      this.moveTrueFitWidget()
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      } else {
        params.delete('variant')
      }

      const url = params.toString().length
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update shared media position.
     * @param {Number} newPosition - New media position.
     */
    handleUpdateCurrentMediaPosition(newPosition) {
      if (newPosition === this.currentMediaPosition) {
        return
      }

      this.currentMediaPosition = newPosition
    },

    /**
     * Update active gallery.
     * @param {String} namespace - Active gallery namespace.
     */
    handleUpdateCurrentNamespace(namespace) {
      this.currentNamespace = namespace
    },

    /**
     * Moves true fit widget.
     */
    moveTrueFitWidget() {
      const widgetElement = document.getElementById('tfc-widget')

      if (!widgetElement) {
        return
      }

      document.getElementById('product-variants-size').appendChild(widgetElement)
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
